import React from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useAuth } from './AuthContext';

function getRemainingDays(expiresAt) {
  if (!expiresAt) return null;

  const expiryDate =
    typeof expiresAt?.toDate === 'function'
      ? expiresAt.toDate()
      : new Date(expiresAt);

  if (Number.isNaN(expiryDate.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(
    expiryDate.getFullYear(),
    expiryDate.getMonth(),
    expiryDate.getDate()
  );

  const diffMs = expiry - today;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getRemainingNotice(remainingDays) {
  if (remainingDays === null) {
    return {
      text: '수강기간 정보 없음',
      style: styles.periodNeutral,
    };
  }

  if (remainingDays < 0) {
    return {
      text: '수강기간 만료',
      style: styles.periodExpired,
    };
  }

  if (remainingDays === 0) {
    return {
      text: '오늘까지 수강 가능',
      style: styles.periodWarning,
    };
  }

  if (remainingDays <= 7) {
    return {
      text: `수강 가능 기간 ${remainingDays}일 남음`,
      style: styles.periodWarning,
    };
  }

  return {
    text: `수강 가능 기간 ${remainingDays}일 남음`,
      style: styles.periodActive,
    };
}

export default function Header() {
  const navigate = useNavigate();
  const { user, studentDoc, isAdmin } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('userInfo');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const remainingDays = getRemainingDays(studentDoc?.expiresAt);
  const remainingNotice = getRemainingNotice(remainingDays);

  return (
    <header style={styles.header}>
      <div style={styles.inner}>
        <div style={styles.left}>
          <div style={styles.iconBox}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.9" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.2" />
            </svg>
          </div>

          <div style={styles.courseBox}>
            <div style={styles.courseTitle}>이러닝 교수설계 입문</div>
            <div style={styles.courseSub}>입문부터 실무까지 · 총 19개 레슨</div>
          </div>
        </div>

        <div style={styles.right}>
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/admin')}
              style={styles.adminBtn}
            >
              관리자 페이지
            </button>
          )}

          <div style={{ ...styles.periodBox, ...remainingNotice.style }}>
            <span style={styles.periodIcon}>⏳</span>
            <span style={styles.periodText}>{remainingNotice.text}</span>
          </div>

          <div style={styles.userEmail}>
            {user?.email || '로그인 사용자'}
          </div>

          <button type="button" onClick={handleLogout} style={styles.logoutBtn}>
            로그아웃
          </button>
        </div>
      </div>
    </header>
  );
}

const styles = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '76px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  inner: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  iconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#eef4ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  courseBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
  },
  courseTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
  },
  courseSub: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '4px',
    whiteSpace: 'nowrap',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  adminBtn: {
    height: '38px',
    padding: '0 14px',
    border: '1px solid #dbe7ff',
    borderRadius: '8px',
    backgroundColor: '#eef4ff',
    color: '#1B4A8C',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  periodBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
  },
  periodIcon: {
    fontSize: '14px',
  },
  periodText: {
    fontSize: '13px',
    fontWeight: '600',
  },
  periodActive: {
    backgroundColor: '#edf7ef',
    borderColor: '#d7eadb',
    color: '#2f6b3d',
  },
  periodWarning: {
    backgroundColor: '#fdf3e7',
    borderColor: '#f3dfc2',
    color: '#854f0b',
  },
  periodExpired: {
    backgroundColor: '#fef2f2',
    borderColor: '#f6d6d6',
    color: '#b91c1c',
  },
  periodNeutral: {
    backgroundColor: '#f3f4f6',
    borderColor: '#e5e7eb',
    color: '#6b7280',
  },
  userEmail: {
    fontSize: '14px',
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  logoutBtn: {
    height: '38px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#111827',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};